import React, {createContext, useCallback, useEffect, useState} from 'react';
import getLanguageConfig from 'api/getLanguageConfig';
import {
	applyOverrides,
	readLangCache,
	writeLangCache,
} from 'utils/strings';

// Identifier for this app's language-config bucket. Matches the se branch.
const APP = 'backoffice';

// Country is resolved inside `getLanguageConfig`, which uses the same
// `process.env.REACT_APP_COUNTRY || 'norway'` default as the other no-branch
// APIs (the se branch defaults to 'sweden').

// Small context exposing an `overridesVersion` counter and a setter. Bumping the
// counter forces any subtree that reads it to re-render after `applyOverrides`
// has mutated the shared `strings` instance in place (which React cannot see on
// its own). no.js and nn.js are FLAT locales, so `applyOverrides` from
// 'utils/strings' flat-merges over both — no namespace/flatten logic here.
export const LanguageContext = createContext({
	overridesVersion: 0,
	bumpOverridesVersion: () => {},
});

export function LanguageProvider({children}) {
	const [overridesVersion, setOverridesVersion] = useState(0);

	const bumpOverridesVersion = useCallback(() => {
		setOverridesVersion((v) => v + 1);
	}, []);

	useEffect(() => {
		let cancelled = false;

		// Apply any cached overrides immediately so the first paint already
		// reflects the latest known strings.
		const cached = readLangCache(APP);
		if (cached) {
			applyOverrides(cached);
		}

		getLanguageConfig(APP).then((overrides) => {
			if (cancelled) {
				return;
			}
			applyOverrides(overrides);
			writeLangCache(APP, overrides);
			bumpOverridesVersion();
		});

		return () => {
			cancelled = true;
		};
	}, [bumpOverridesVersion]);

	return (
		<LanguageContext.Provider value={{overridesVersion, bumpOverridesVersion}}>
			{children}
		</LanguageContext.Provider>
	);
}
